'use client';

import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash, Info, Function as FnIcon, Hash } from '@phosphor-icons/react';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';
import { useNodeSchema } from '../../../contexts/SchemaContext';
import { aiApi } from '../../../services/api';
import type { Column } from '../../../types';
import { getNodeDisplayLabel } from '../../../utils/nodeDisplayLabel';

// ── Types ─────────────────────────────────────────────────────────────────────

type CalculationTarget = 'column' | 'row';

interface Expression {
  column: string;
  formula: string;
}

interface RowExpression {
  formula: string;
  descriptionName: string;
  descriptionColumnName?: string;
}

/** Normalize API / persisted row expression objects (camelCase or snake_case). */
function normalizeRowExpressions(raw: unknown[] | undefined): RowExpression[] {
  if (!raw?.length) {
    return [{ formula: '', descriptionName: '' }];
  }
  return raw.map((item) => {
    if (!item || typeof item !== 'object') {
      return { formula: '', descriptionName: '' };
    }
    const o = item as Record<string, unknown>;
    const descCol = o.descriptionColumnName ?? o.description_column_name;
    const descColStr = typeof descCol === 'string' ? descCol.trim() : '';
    const descName =
      typeof o.descriptionName === 'string'
        ? o.descriptionName
        : typeof o.description_name === 'string'
          ? o.description_name
          : '';
    return {
      formula: typeof o.formula === 'string' ? o.formula : '',
      descriptionName: descName,
      descriptionColumnName: descColStr || undefined,
    };
  });
}

interface ComputeConfigProps {
  nodeId: string;
  config: { 
    calculationTarget?: CalculationTarget;
    targetColumn?: string;
    expressions?: Expression[];
    rowExpressions?: RowExpression[];
  };
}

interface FormulaEditorDialogProps {
  open: boolean;
  initialFormula: string;
  availableColumns: string[];
  onConfirm: (formula: string) => void;
  onClose: () => void;
}

// ── Formula Editor Dialog ─────────────────────────────────────────────────────

function FormulaEditorDialog({
  open,
  initialFormula,
  availableColumns,
  onConfirm,
  onClose,
}: FormulaEditorDialogProps) {
  const { t } = useTranslation();
  const [formula, setFormula] = useState(initialFormula || '');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setFormula(initialFormula || '');
      setAiPrompt('');
      setAiError(null);
    }
  }, [open, initialFormula]);

  const insertAtCursor = (text: string, cursorOffset = 0) => {
    const ta = textareaRef.current;
    if (!ta) {
      setFormula((prev) => prev + text);
      return;
    }
    const start = ta.selectionStart ?? formula.length;
    const end = ta.selectionEnd ?? formula.length;
    const next = formula.slice(0, start) + text + formula.slice(end);
    setFormula(next);
    const nextPos = start + text.length + cursorOffset;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(nextPos, nextPos);
    });
  };

  const handleInsertColumn = (name: string) => {
    // 字段引用格式：$`字段名`（仅反引号包裹，无反斜杠）
    insertAtCursor('$' + '`' + name + '`');
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    try {
      setAiLoading(true);
      setAiError(null);
      const res = await aiApi.generateFormula(aiPrompt, availableColumns);
      if (!res.data.formula) {
        throw new Error(t('nodes.config.compute.aiNoFormula'));
      }
      setFormula(res.data.formula);
    } catch (err) {
      setAiError((err as Error).message || t('nodes.config.compute.aiError'));
    } finally {
      setAiLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FnIcon className="w-4 h-4 text-emerald-500" weight="bold" />
            <span className="text-sm font-semibold text-zinc-800">{t('nodes.config.compute.setFormula')}</span>
          </div>
          <button
            onClick={onClose}
            className="text-xs text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 px-2 py-1 rounded-lg"
          >
            {t('nodes.config.compute.close')}
          </button>
        </div>

        {/* Body */}
        <div className="grid grid-cols-3 gap-0 divide-x divide-zinc-100">
          {/* Left: fields */}
          <div className="p-3 space-y-2">
            <div className="flex items-center gap-1.5 mb-1">
              <Hash className="w-3 h-3 text-emerald-500" weight="bold" />
              <span className="text-xs font-medium text-zinc-600">{t('nodes.config.compute.availableColumns')}</span>
            </div>
            <div className="border border-zinc-200 rounded-lg max-h-64 overflow-y-auto p-1 bg-zinc-50">
              {availableColumns.length === 0 ? (
                <div className="text-xs text-zinc-400 text-center py-4">
                  {t('nodes.config.compute.noColumns')}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {availableColumns.map((col) => (
                    <button
                      key={col}
                      type="button"
                      onClick={() => handleInsertColumn(col)}
                      className="px-2 py-1 rounded-md bg-white border border-zinc-200 text-[11px] text-zinc-700 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                    >
                      {col}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[10px] text-zinc-400">
              {t('nodes.config.compute.columnInsertHint')}
            </p>
          </div>

          {/* Middle: operators & textarea */}
          <div className="p-3 space-y-3">
            {/* Operator buttons */}
            <div className="space-y-1">
              <span className="text-xs font-medium text-zinc-600">{t('nodes.config.compute.operators')}</span>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: t('nodes.config.compute.opAdd'), token: ' + ' },
                  { label: t('nodes.config.compute.opSub'), token: ' - ' },
                  { label: t('nodes.config.compute.opMul'), token: ' * ' },
                  { label: t('nodes.config.compute.opDiv'), token: ' / ' },
                  { label: t('nodes.config.compute.opParen'), token: '()', cursorOffset: -1 },
                ].map((btn) => (
                  <button
                    key={btn.label}
                    type="button"
                    onClick={() => insertAtCursor(btn.token, btn.cursorOffset ?? 0)}
                    className="px-2 py-1 rounded-md bg-zinc-50 border border-zinc-200 text-[11px] text-zinc-700 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                  >
                    {btn.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: t('nodes.config.compute.funcAbs'), token: 'ABS()', cursorOffset: -1 },
                  { label: t('nodes.config.compute.funcSqrt'), token: 'SQRT()', cursorOffset: -1 },
                  { label: t('nodes.config.compute.funcRound'), token: 'ROUND()', cursorOffset: -1 },
                  { label: t('nodes.config.compute.funcFloor'), token: 'FLOOR()', cursorOffset: -1 },
                  { label: t('nodes.config.compute.funcCeil'), token: 'CEIL()', cursorOffset: -1 },
                  { label: t('nodes.config.compute.funcPower'), token: 'POWER(,)', cursorOffset: -2 },
                  { label: t('nodes.config.compute.funcMod'), token: 'MOD(,)', cursorOffset: -2 },
                ].map((btn) => (
                  <button
                    key={btn.label}
                    type="button"
                    onClick={() => insertAtCursor(btn.token, btn.cursorOffset ?? 0)}
                    className="px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-700 hover:bg-emerald-100 transition-colors"
                  >
                    {btn.label}
                  </button>
                ))}
              </div>

              {/* Array functions */}
              <div className="pt-2 border-t border-zinc-100">
                <div className="text-[10px] text-zinc-500 mb-1.5">{t('nodes.config.compute.arrayFunctions')}</div>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { label: t('nodes.config.compute.funcSum'), token: 'SUM()', cursorOffset: -1 },
                    { label: t('nodes.config.compute.funcAvg'), token: 'AVG()', cursorOffset: -1 },
                    { label: t('nodes.config.compute.funcMax'), token: 'MAX()', cursorOffset: -1 },
                    { label: t('nodes.config.compute.funcMin'), token: 'MIN()', cursorOffset: -1 },
                    { label: t('nodes.config.compute.funcMedian'), token: 'MEDIAN()', cursorOffset: -1 },
                    { label: t('nodes.config.compute.funcStd'), token: 'STD()', cursorOffset: -1 },
                    { label: t('nodes.config.compute.funcRange'), token: 'RANGE()', cursorOffset: -1 },
                    { label: t('nodes.config.compute.funcNormalize'), token: 'NORMALIZE()', cursorOffset: -1 },
                  ].map((btn) => (
                    <button
                      key={btn.label}
                      type="button"
                      onClick={() => insertAtCursor(btn.token, btn.cursorOffset ?? 0)}
                      className="px-2 py-1 rounded-md bg-blue-50 border border-blue-200 text-[11px] text-blue-700 hover:bg-blue-100 transition-colors"
                      title={t('nodes.config.compute.arrayFuncHint')}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Formula textarea */}
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-zinc-600">{t('nodes.config.compute.formulaEdit')}</span>
              <textarea
                ref={textareaRef}
                rows={4}
                value={formula}
                onChange={(e) => setFormula(e.target.value)}
                placeholder={t('nodes.config.compute.formulaPlaceholder')}
                spellCheck={false}
                className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none font-mono placeholder:text-zinc-400"
              />
            </div>
          </div>

          {/* Right: preview & AI helper */}
          <div className="p-3 space-y-3">
            <div className="space-y-1">
              <span className="text-xs font-medium text-zinc-600">{t('nodes.config.compute.formulaPreview')}</span>
              <div className="min-h-[72px] max-h-28 overflow-y-auto bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-[11px] font-mono text-zinc-700 break-all">
                {formula || <span className="text-zinc-400">{t('nodes.config.compute.noFormula')}</span>}
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-zinc-600">{t('nodes.config.compute.aiAssistant')}</span>
              </div>
              <textarea
                rows={3}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder={t('nodes.config.compute.aiPlaceholder')}
                className="w-full text-xs bg-white border border-zinc-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none placeholder:text-zinc-400"
              />
              {aiError && (
                <div className="text-[10px] text-red-500">
                  {aiError}
                </div>
              )}
              <button
                type="button"
                disabled={aiLoading}
                onClick={handleAiGenerate}
                className="mt-1 inline-flex items-center justify-center px-3 py-1.5 text-[11px] font-medium rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {aiLoading ? t('nodes.config.compute.aiGenerating') : t('nodes.config.compute.aiGenerate')}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-zinc-100 flex justify-end gap-2 bg-zinc-50">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
          >
            {t('nodes.config.compute.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm(formula.trim());
              onClose();
            }}
            className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            {t('nodes.config.compute.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ComputeConfig({ nodeId, config }: ComputeConfigProps) {
  const { t, i18n } = useTranslation();
  const { nodes, updateNodeConfig } = useWorkflowStore();
  const { register } = useNodeSchema(nodeId);
  const inputColumns = useNodeInputColumns(nodeId);

  // Calculation target: 'column' (default) or 'row'
  const [calculationTarget, setCalculationTarget] = useState<CalculationTarget>(
    config.calculationTarget || 'column'
  );
  
  // For row calculation: target column to store results
  const [targetColumn, setTargetColumn] = useState<string>(config.targetColumn || '');

  // Column expressions (default mode)
  const [expressions, setExpressions] = useState<Expression[]>(
    config.expressions?.length ? config.expressions : [{ column: '', formula: '' }]
  );
  
  // Row expressions (row calculation mode)
  const [rowExpressions, setRowExpressions] = useState<RowExpression[]>(() =>
    normalizeRowExpressions(config.rowExpressions as unknown[] | undefined)
  );
  
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);

  // Keep local state in sync when switching nodes or when persisted config changes (e.g. reload).
  const configSyncKey = useMemo(
    () =>
      JSON.stringify({
        ct: config.calculationTarget,
        tc: config.targetColumn,
        expr: config.expressions,
        row: config.rowExpressions,
      }),
    [config.calculationTarget, config.targetColumn, config.expressions, config.rowExpressions]
  );

  useEffect(() => {
    if (config.calculationTarget === 'row' || config.calculationTarget === 'column') {
      setCalculationTarget(config.calculationTarget);
    }
    if (typeof config.targetColumn === 'string') {
      setTargetColumn(config.targetColumn);
    } else if (config.targetColumn === undefined || config.targetColumn === null) {
      setTargetColumn('');
    }
    if (Array.isArray(config.expressions) && config.expressions.length > 0) {
      setExpressions(config.expressions as Expression[]);
    }
    if (Array.isArray(config.rowExpressions) && config.rowExpressions.length > 0) {
      setRowExpressions(normalizeRowExpressions(config.rowExpressions as unknown[]));
    }
  }, [nodeId, configSyncKey]);

  // 上游列定义与名称列表
  const upstreamColumns: Column[] = useMemo(
    () => (inputColumns ?? []).map((c) => c.column),
    [inputColumns]
  );
  const availableColumns = useMemo(
    () => upstreamColumns.map((c) => c.name),
    [upstreamColumns]
  );

  const rowExpressionsRef = useRef(rowExpressions);
  rowExpressionsRef.current = rowExpressions;

  const targetColumnRef = useRef(targetColumn);
  targetColumnRef.current = targetColumn;

  // Align persisted targetColumn with upstream schema names (NFC / trim) so the select and API stay consistent.
  useEffect(() => {
    if (calculationTarget !== 'row' || !targetColumn || availableColumns.length === 0) {
      return;
    }
    const hit = availableColumns.find(
      (c) =>
        c === targetColumn ||
        c.trim().normalize('NFC') === targetColumn.trim().normalize('NFC')
    );
    if (hit && hit !== targetColumn) {
      setTargetColumn(hit);
      updateNodeConfig(nodeId, {
        type: 'compute',
        params: {
          calculationTarget: 'row',
          targetColumn: hit,
          rowExpressions: rowExpressionsRef.current,
        },
      });
    }
  }, [calculationTarget, targetColumn, availableColumns, nodeId, updateNodeConfig]);

  // Row mode: default「结果存放列」to the first upstream column when unset so the summary row has a label column.
  useEffect(() => {
    if (calculationTarget !== 'row' || targetColumn || availableColumns.length === 0) {
      return;
    }
    const first = availableColumns[0];
    setTargetColumn(first);
    updateNodeConfig(nodeId, {
      type: 'compute',
      params: {
        calculationTarget: 'row',
        targetColumn: first,
        rowExpressions: rowExpressionsRef.current,
      },
    });
  }, [calculationTarget, targetColumn, availableColumns, nodeId, updateNodeConfig]);

  // Schema registration for column mode
  const syncColumnSchema = (nextExpressions: Expression[]) => {
    if (!upstreamColumns.length) return;
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const baseCols = [...upstreamColumns];
    const nextCols = [...baseCols];

    nextExpressions.forEach((expr, idx) => {
      const name = expr.column?.trim();
      if (!name) return;
      if (nextCols.find((c) => c.name === name)) return;
      nextCols.push({
        name,
        type: 'number',
        nullable: true,
        index: baseCols.length + idx,
      });
    });

    register({
      nodeType: node.type,
      nodeName: getNodeDisplayLabel(node.type, node.data.label, t, i18n),
      columns: nextCols,
    });
  };

  // Handle calculation target change (avoid stale closure: write explicit params instead of syncConfig)
  const handleTargetChange = (target: CalculationTarget) => {
    setCalculationTarget(target);
    if (target === 'row') {
      const tc = targetColumn || availableColumns[0] || '';
      if (!targetColumn && tc) {
        setTargetColumn(tc);
      }
      updateNodeConfig(nodeId, {
        type: 'compute',
        params: {
          calculationTarget: 'row',
          targetColumn: tc,
          rowExpressions,
        },
      });
      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        register({
          nodeType: node.type,
          nodeName: getNodeDisplayLabel(node.type, node.data.label, t, i18n),
          columns: upstreamColumns,
        });
      }
    } else {
      updateNodeConfig(nodeId, {
        type: 'compute',
        params: {
          calculationTarget: 'column',
          expressions,
        },
      });
      syncColumnSchema(expressions);
    }
  };

  // Column mode handlers
  const saveColumnExpressions = (next: Expression[]) => {
    updateNodeConfig(nodeId, { 
      type: 'compute', 
      params: { 
        calculationTarget: 'column',
        expressions: next 
      } 
    });
    syncColumnSchema(next);
  };

  const updateExpression = (index: number, patch: Partial<Expression>) => {
    const next = expressions.map((e, i) => (i === index ? { ...e, ...patch } : e));
    setExpressions(next);
    saveColumnExpressions(next);
  };

  const addExpression = () => {
    const next = [...expressions, { column: '', formula: '' }];
    setExpressions(next);
    saveColumnExpressions(next);
  };

  const removeExpression = (index: number) => {
    const next = expressions.filter((_, i) => i !== index);
    setExpressions(next);
    saveColumnExpressions(next);
  };

  // Row mode handlers
  const saveRowExpressions = (next: RowExpression[]) => {
    updateNodeConfig(nodeId, { 
      type: 'compute', 
      params: { 
        calculationTarget: 'row',
        targetColumn: targetColumnRef.current,
        rowExpressions: next 
      } 
    });
  };

  const updateRowExpression = (index: number, patch: Partial<RowExpression>) => {
    const next = rowExpressions.map((e, i) => (i === index ? { ...e, ...patch } : e));
    setRowExpressions(next);
    saveRowExpressions(next);
  };

  const addRowExpression = () => {
    const next = [...rowExpressions, { formula: '', descriptionName: '' }];
    setRowExpressions(next);
    saveRowExpressions(next);
  };

  const removeRowExpression = (index: number) => {
    const next = rowExpressions.filter((_, i) => i !== index);
    setRowExpressions(next);
    saveRowExpressions(next);
  };

  // Update target column
  const handleTargetColumnChange = (col: string) => {
    setTargetColumn(col);
    if (calculationTarget === 'row') {
      updateNodeConfig(nodeId, { 
        type: 'compute', 
        params: { 
          calculationTarget: 'row',
          targetColumn: col,
          rowExpressions 
        } 
      });
    }
  };

  return (
    <div className="space-y-4">
      {/* Calculation Target Selector */}
      <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-100">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-zinc-700">{t('nodes.config.compute.calculationTarget')}</span>
        </div>
        <select
          value={calculationTarget}
          onChange={(e) => handleTargetChange(e.target.value as CalculationTarget)}
          className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
        >
          <option value="column">{t('nodes.config.compute.targetColumn')}</option>
          <option value="row">{t('nodes.config.compute.targetRow')}</option>
        </select>
        <p className="mt-2 text-[10px] text-zinc-500">
          {calculationTarget === 'column' 
            ? t('nodes.config.compute.targetColumnHint')
            : t('nodes.config.compute.targetRowHint')
          }
        </p>
      </div>

      {/* Row Mode: Target Column Selector */}
      {calculationTarget === 'row' && (
        <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-zinc-700">{t('nodes.config.compute.resultTargetColumn')}</span>
          </div>
          <select
            value={targetColumn}
            onChange={(e) => handleTargetColumnChange(e.target.value)}
            className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          >
            <option value="">{t('nodes.config.compute.selectTargetColumn')}</option>
            {availableColumns.map((col) => (
              <option key={col} value={col}>{col}</option>
            ))}
          </select>
          <p className="mt-1.5 text-[10px] text-zinc-500">
            {t('nodes.config.compute.resultTargetColumnHint')}
          </p>
        </div>
      )}

      {/* Column Mode: Expression List */}
      {calculationTarget === 'column' && (
        <>
          <AnimatePresence>
            {expressions.map((expr, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                className="p-3 bg-zinc-50 rounded-xl border border-zinc-100 space-y-2"
              >
                {/* 列名 + 删除 */}
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder={t('nodes.config.compute.newColumnPlaceholder')}
                      value={expr.column}
                      onChange={(e) => updateExpression(index, { column: e.target.value })}
                      className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
                    />
                  </div>
                  {expressions.length > 1 && (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => removeExpression(index)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                    >
                      <Trash className="w-4 h-4" />
                    </motion.button>
                  )}
                </div>

                {/* 公式按钮 + 预览 */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <FnIcon className="w-3 h-3 text-emerald-500" weight="bold" />
                    <span className="text-[11px] font-medium text-zinc-500">{t('nodes.config.compute.formula')}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditingIndex(index)}
                    className="px-2 py-1 text-[11px] rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  >
                    {t('nodes.config.compute.setFormula')}
                  </button>
                </div>
                <div className="min-h-[28px] max-h-20 overflow-y-auto bg-white border border-dashed border-zinc-200 rounded-lg px-2.5 py-1.5 text-[11px] font-mono text-zinc-600 break-all">
                  {expr.formula ? (
                    expr.formula
                  ) : (
                    <span className="text-zinc-400">{t('nodes.config.compute.noFormulaHint')}</span>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* 添加按钮 */}
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={addExpression}
            className="flex items-center gap-2 w-full justify-center px-4 py-2.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('nodes.config.compute.addComputeField')}
          </motion.button>
        </>
      )}

      {/* Row Mode: Row Expression List */}
      {calculationTarget === 'row' && (
        <>
          <AnimatePresence>
            {rowExpressions.map((expr, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                className="p-3 bg-zinc-50 rounded-xl border border-zinc-100 space-y-3"
              >
                {/* Description Name */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-medium text-zinc-600">{t('nodes.config.compute.descriptionName')}</span>
                  </div>
                  <input
                    type="text"
                    placeholder={t('nodes.config.compute.descriptionNamePlaceholder')}
                    value={expr.descriptionName}
                    onChange={(e) => updateRowExpression(index, { descriptionName: e.target.value })}
                    className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 placeholder:text-zinc-400"
                  />
                </div>

                {/* Description Column Name (optional) */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-medium text-zinc-600">{t('nodes.config.compute.descriptionColumnName')}</span>
                    <span className="text-[10px] text-zinc-400">{t('nodes.config.compute.optional')}</span>
                  </div>
                  <select
                    value={expr.descriptionColumnName || ''}
                    onChange={(e) => updateRowExpression(index, { descriptionColumnName: e.target.value || undefined })}
                    className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  >
                    <option value="">{t('nodes.config.compute.useDescriptionName')}</option>
                    {availableColumns.map((col) => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-zinc-400">
                    {t('nodes.config.compute.descriptionColumnNameHint')}
                  </p>
                </div>

                {/* Formula */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <FnIcon className="w-3 h-3 text-emerald-500" weight="bold" />
                      <span className="text-[11px] font-medium text-zinc-500">{t('nodes.config.compute.formula')}</span>
                    </div>
                    {rowExpressions.length > 1 && (
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => removeRowExpression(index)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash className="w-3.5 h-3.5" />
                      </motion.button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditingRowIndex(index)}
                    className="w-full text-left px-3 py-2 text-[11px] rounded-lg border border-dashed border-zinc-300 bg-white hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
                  >
                    {expr.formula ? (
                      <span className="font-mono text-zinc-700 break-all">{expr.formula}</span>
                    ) : (
                      <span className="text-zinc-400">{t('nodes.config.compute.noFormulaHint')}</span>
                    )}
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Add Row Expression Button */}
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={addRowExpression}
            className="flex items-center gap-2 w-full justify-center px-4 py-2.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('nodes.config.compute.addComputeRow')}
          </motion.button>
        </>
      )}

      {/* 使用说明 */}
      <div className="space-y-2 p-3 bg-blue-50 rounded-xl border border-blue-100">
        <div className="flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
          <span className="text-xs font-medium text-blue-700">{t('nodes.config.compute.usageTitle')}</span>
        </div>
        <ul className="text-xs text-blue-700 space-y-1 ml-5 list-disc">
          {calculationTarget === 'column' ? (
            <>
              <li>{t('nodes.config.compute.usageFieldFormat')}</li>
              <li>
                {t('nodes.config.compute.usageFunctions')}
                <div>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">ABS</code>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">SQRT</code>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">ROUND</code>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">FLOOR</code>
                </div>
                <div>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">CEIL</code>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">POWER</code>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">MOD</code>
                </div>
              </li>
              <li>{t('nodes.config.compute.usageAi')}</li>
            </>
          ) : (
            <>
              <li>{t('nodes.config.compute.usageRowFieldFormat')}</li>
              <li>
                {t('nodes.config.compute.usageRowFunctions')}
                <div>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">SUM</code>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">AVG</code>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">MAX</code>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">MIN</code>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">MEDIAN</code>
                </div>
                <div>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">STD</code>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">RANGE</code>
                  <code className="bg-blue-100 px-1 mx-0.5 rounded font-mono">NORMALIZE</code>
                </div>
              </li>
              <li>{t('nodes.config.compute.usageRowExample')}</li>
            </>
          )}
        </ul>
      </div>

      {/* Formula Editor Dialog for Column Mode */}
      {calculationTarget === 'column' && editingIndex !== null && expressions[editingIndex] && (
        <FormulaEditorDialog
          open={editingIndex !== null}
          initialFormula={expressions[editingIndex].formula}
          availableColumns={availableColumns}
          onConfirm={(formula) => updateExpression(editingIndex, { formula })}
          onClose={() => setEditingIndex(null)}
        />
      )}

      {/* Formula Editor Dialog for Row Mode */}
      {calculationTarget === 'row' && editingRowIndex !== null && rowExpressions[editingRowIndex] && (
        <FormulaEditorDialog
          open={editingRowIndex !== null}
          initialFormula={rowExpressions[editingRowIndex].formula}
          availableColumns={availableColumns}
          onConfirm={(formula) => updateRowExpression(editingRowIndex, { formula })}
          onClose={() => setEditingRowIndex(null)}
        />
      )}
    </div>
  );
}
